import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, X, ChevronUp, ChevronDown, Edit2, Trash2, Filter, RefreshCw, Upload, Download, AlertCircle } from 'lucide-react';
import API_BASE_URL from '../config';
import Swal from 'sweetalert2';
const ItemMaintenance = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filterField, setFilterField] = useState('all');
  const [editingItem, setEditingItem] = useState(null);
  const [importData, setImportData] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    id: 0,
    itemCode: '',
    itemDesciption: '',
    supplierAddress: '',
    category: '',        // ✅ Add
    oumSystem: '',       // ✅ Add  
    oumActual: ''        // ✅ Add
  });

  const ITEMS_API = `${API_BASE_URL}api/Items`;

  // Fetch items from API
  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(ITEMS_API);
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  // Create new item
  const createItem = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(ITEMS_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const newItem = await response.json();
        setItems([...items, newItem]);
        closeModal();
        fetchItems();
      }
    } catch (error) {
      console.error('Error creating item:', error);
    }
    setLoading(false);
  };

  // Update item
  const updateItem = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${ITEMS_API}/${editingItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        closeModal();
        fetchItems();
      }
    } catch (error) {
      console.error('Error updating item:', error);
    }
    setLoading(false);
  };

  // Delete item
  const deleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    setLoading(true);
    try {
      const response = await fetch(`${ITEMS_API}/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setItems(items.filter(item => item.id !== id));
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    }
    setLoading(false);
  };

  // Delete all items
  const deleteAllItems = async () => {
    if (items.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'No Items',
        text: 'No items to delete!',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete All Items?',
      html: `You are about to delete <strong>${items.length}</strong> items.<br/>This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete all!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    const doubleConfirm = await Swal.fire({
      title: 'Final Warning!',
      text: 'This will permanently delete all items. Are you absolutely sure?',
      icon: 'error',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'DELETE ALL',
      cancelButtonText: 'Cancel'
    });

    if (!doubleConfirm.isConfirmed) return;

    setLoading(true);

    let deletedCount = 0;
    const totalItems = items.length;

    Swal.fire({
      title: 'Deleting Items',
      html: `
      <div style="text-align: center;">
        <div style="font-size: 48px; font-weight: bold; color: #ef4444; margin: 20px 0;">
          ${deletedCount}/${totalItems}
        </div>
        <div style="color: #64748b;">Deleting items...</div>
      </div>
    `,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      let successCount = 0;
      let failCount = 0;

      for (const item of items) {
        try {
          const response = await fetch(`${ITEMS_API}/${item.id}`, {
            method: 'DELETE'
          });
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
          console.error(`Error deleting item ${item.id}:`, error);
        }

        deletedCount++;
        Swal.update({
          html: `
          <div style="text-align: center;">
            <div style="font-size: 48px; font-weight: bold; color: #ef4444; margin: 20px 0;">
              ${deletedCount}/${totalItems}
            </div>
            <div style="color: #64748b;">Deleting items...</div>
          </div>
        `
        });
      }

      await fetchItems();

      Swal.fire({
        icon: successCount === totalItems ? 'success' : 'warning',
        title: 'Deletion Complete',
        html: `
        <div style="text-align: left;">
          <p>✅ Deleted: <strong>${successCount}</strong></p>
          ${failCount > 0 ? `<p>❌ Failed: <strong>${failCount}</strong></p>` : ''}
        </div>
      `,
        confirmButtonColor: '#3b82f6'
      });
    } catch (error) {
      console.error('Error deleting all items:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while deleting items!',
        confirmButtonColor: '#ef4444'
      });
    }

    setLoading(false);
  };

  // Export to CSV
  const handleExport = () => {
    if (items.length === 0) {
      alert('No items to export!');
      return;
    }

    const headers = [
      'id',
      'itemCode',
      'itemDesciption',
      'supplierAddress',
      'category',      // ✅ Add
      'oumSystem',     // ✅ Add
      'oumActual'      // ✅ Add
    ];
    const csvContent = [
      headers.join(','),
      ...items.map(item =>
        headers.map(header => `"${item[header] || ''}"`).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `items_export_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file import
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        alert('Invalid CSV file!');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const data = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const item = {};
        headers.forEach((header, index) => {
          item[header] = values[index] || '';
        });
        data.push(item);
      }

      checkDuplicates(data);
      setShowImportModal(true);
    };

    reader.readAsText(file);
  };

  // Check for duplicates
  const checkDuplicates = (newItems) => {
    const dupes = [];
    const nonDupes = [];

    newItems.forEach(newItem => {
      const existingItem = items.find(item =>
        item.itemCode.toLowerCase() === newItem.itemCode.toLowerCase()
      );

      if (existingItem) {
        dupes.push({
          ...newItem,
          existingId: existingItem.id,
          existing: existingItem,
          action: 'skip' // default action
        });
      } else {
        nonDupes.push(newItem);
      }
    });

    setImportData(nonDupes);
    setDuplicates(dupes);
  };

  // Handle duplicate action change
  const handleDuplicateAction = (index, action) => {
    const updated = [...duplicates];
    updated[index].action = action;
    setDuplicates(updated);
  };

  // Process import
  const processImport = async () => {
    setLoading(true);

    const totalItems = importData.length + duplicates.filter(d => d.action !== 'skip').length;
    let processedCount = 0;

    Swal.fire({
      title: 'Importing Items',
      html: `
      <div style="text-align: center;">
        <div style="font-size: 48px; font-weight: bold; color: #3b82f6; margin: 20px 0;">
          ${processedCount}/${totalItems}
        </div>
        <div style="color: #64748b;">Processing items...</div>
      </div>
    `,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      // ✅ Process non-duplicates
      for (const item of importData) {
        await fetch(ITEMS_API, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'accept': 'text/plain'
          },
          body: JSON.stringify({
            id: 0,
            itemCode: item.itemCode,
            itemDesciption: item.itemDesciption,
            supplierAddress: item.supplierAddress,
            category: item.category || '',        // ✅ ADD
            oumSystem: item.oumSystem || '',      // ✅ ADD
            oumActual: item.oumActual || ''       // ✅ ADD
          })
        });

        processedCount++;
        Swal.update({
          html: `
          <div style="text-align: center;">
            <div style="font-size: 48px; font-weight: bold; color: #3b82f6; margin: 20px 0;">
              ${processedCount}/${totalItems}
            </div>
            <div style="color: #64748b;">Processing items...</div>
          </div>
        `
        });
      }

      // ✅ Process duplicates
      for (const dup of duplicates) {
        if (dup.action === 'update') {
          await fetch(`${ITEMS_API}/${dup.existingId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'accept': 'text/plain'
            },
            body: JSON.stringify({
              id: dup.existingId,
              itemCode: dup.itemCode,
              itemDesciption: dup.itemDesciption,
              supplierAddress: dup.supplierAddress,
              category: dup.category || '',        // ✅ ADD
              oumSystem: dup.oumSystem || '',      // ✅ ADD
              oumActual: dup.oumActual || ''       // ✅ ADD
            })
          });
          processedCount++;
        } else if (dup.action === 'delete') {
          await fetch(`${ITEMS_API}/${dup.existingId}`, {
            method: 'DELETE'
          });
          processedCount++;
        }

        if (dup.action !== 'skip') {
          Swal.update({
            html: `
            <div style="text-align: center;">
              <div style="font-size: 48px; font-weight: bold; color: #3b82f6; margin: 20px 0;">
                ${processedCount}/${totalItems}
              </div>
              <div style="color: #64748b;">Processing items...</div>
            </div>
          `
          });
        }
      }

      await fetchItems();
      closeImportModal();

      Swal.fire({
        icon: 'success',
        title: 'Import Complete!',
        text: `Successfully imported ${totalItems} items`,
        confirmButtonColor: '#3b82f6'
      });
    } catch (error) {
      console.error('Error importing items:', error);
      Swal.fire({
        icon: 'error',
        title: 'Import Failed',
        text: 'An error occurred during import',
        confirmButtonColor: '#ef4444'
      });
    }

    setLoading(false);
  };
  // Open modal for create
  const openCreateModal = () => {
    setEditingItem(null);
    setFormData({
      id: 0,
      itemCode: '',
      itemDesciption: '',
      supplierAddress: ''
    });
    setShowModal(true);
  };

  // Open modal for edit
  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      id: 0,
      itemCode: '',
      itemDesciption: '',
      supplierAddress: ''
    });
  };

  // Close import modal
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportData([]);
    setDuplicates([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Filter items based on search term and filter field
  const filteredItems = items.filter(item => {
    const searchLower = searchTerm.toLowerCase();

    if (filterField === 'all') {
      return (
        item.itemCode?.toLowerCase().includes(searchLower) ||
        item.itemDesciption?.toLowerCase().includes(searchLower) ||
        item.supplierAddress?.toLowerCase().includes(searchLower) ||
        item.category?.toLowerCase().includes(searchLower) ||      // ✅ Add
        item.oumSystem?.toLowerCase().includes(searchLower) ||     // ✅ Add
        item.oumActual?.toLowerCase().includes(searchLower)        // ✅ Add
      );
    } else {
      return item[filterField]?.toLowerCase().includes(searchLower);
    }
  });

  // Sort items
  const sortedItems = [...filteredItems].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();

    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="opacity-30" size={16} />;
    return sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f8f9fa',
      padding: '2.5rem 1.5rem',
      fontFamily: '"Outfit", -apple-system, sans-serif'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
        
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .glass-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .btn {
          padding: 0.75rem 1.5rem;
          border-radius: 10px;
          border: none;
          font-weight: 500;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-family: 'Outfit', sans-serif;
        }

        .btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }

        .btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
        }

        .btn-success:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        }

        .btn-warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
        }

        .btn-warning:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
        }

        .btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
        }

        .btn-danger:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
        }

        .btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .btn-secondary {
          background: #ffffff;
          color: #475569;
          border: 1px solid #e2e8f0;
        }

        .btn-secondary:hover {
          background: #f8f9fa;
          border-color: #cbd5e1;
          transform: translateY(-1px);
        }

        .input-field {
          width: 100%;
          padding: 0.875rem 1rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          color: #1e293b;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          font-family: 'Outfit', sans-serif;
        }

        .input-field:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .input-field::placeholder {
          color: #94a3b8;
        }

        .table-container {
          overflow-x: auto;
          border-radius: 12px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
        }

        th {
          background: #f8f9fa;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #475569;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s ease;
          border-bottom: 2px solid #e2e8f0;
        }

        th:hover {
          background: #f1f5f9;
        }

        td {
          padding: 1rem;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          font-size: 0.9rem;
        }

        tr:hover {
          background: #f8f9fa;
        }

        tr:last-child td {
          border-bottom: none;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .modal-content {
          animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-content-large {
          animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          max-width: 900px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .pagination {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
        }

        .page-btn {
          padding: 0.5rem 0.875rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          font-family: 'Outfit', sans-serif;
          min-width: 40px;
        }

        .page-btn:hover:not(:disabled) {
          background: #f8f9fa;
          border-color: #3b82f6;
          transform: translateY(-1px);
        }

        .page-btn.active {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          border-color: #3b82f6;
          color: white;
        }

        .page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .spinner {
          border: 3px solid #e2e8f0;
          border-top: 3px solid #3b82f6;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .icon-btn {
          padding: 0.5rem;
          background: transparent;
          border: none;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .icon-btn:hover {
          background: #f1f5f9;
        }

        .icon-btn.edit:hover {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .icon-btn.delete:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        select {
          padding: 0.625rem 2.5rem 0.625rem 1rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          color: #1e293b;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Outfit', sans-serif;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23475569' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.875rem center;
        }

        select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        label {
          display: block;
          margin-bottom: 0.5rem;
          color: #475569;
          font-weight: 500;
          font-size: 0.9rem;
        }

        .badge {
          display: inline-block;
          padding: 0.375rem 0.75rem;
          background: #eff6ff;
          color: #3b82f6;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .badge-warning {
          background: #fef3c7;
          color: #d97706;
        }

        .badge-success {
          background: #d1fae5;
          color: #059669;
        }

        .badge-danger {
          background: #fee2e2;
          color: #dc2626;
        }

        .alert {
          padding: 1rem;
          border-radius: 10px;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .alert-warning {
          background: #fef3c7;
          border: 1px solid #fde68a;
          color: #92400e;
        }

        .duplicate-item {
          background: #fef3c7;
          border: 1px solid #fde68a;
          padding: 1rem;
          border-radius: 10px;
          margin-bottom: 0.75rem;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.5rem;
        }

        .action-btn {
          padding: 0.5rem 1rem;
          border-radius: 6px;
          border: none;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Outfit', sans-serif;
        }

        .action-btn.active {
          font-weight: 600;
        }

        .action-btn.skip {
          background: #e2e8f0;
          color: #475569;
        }

        .action-btn.skip.active {
          background: #cbd5e1;
        }

        .action-btn.update {
          background: #dbeafe;
          color: #1e40af;
        }

        .action-btn.update.active {
          background: #3b82f6;
          color: white;
        }

        .action-btn.delete {
          background: #fee2e2;
          color: #991b1b;
        }

        .action-btn.delete.active {
          background: #ef4444;
          color: white;
        }

        input[type="file"] {
          display: none;
        }
      `}</style>

      <div style={{ maxWidth: '1500px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem',
            letterSpacing: '-0.02em'
          }}>
            Item Maintenance
          </h1>
          <p style={{ color: '#64748b', fontSize: '1.05rem' }}>
            Manage your inventory items with ease
          </p>
        </div>

        {/* Controls */}
        <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <Search
                size={18}
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#94a3b8'
                }}
              />
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field"
                style={{ paddingLeft: '2.75rem' }}
              />
            </div>

            {/* Filter */}
            <div style={{ position: 'relative' }}>
              <Filter
                size={18}
                style={{
                  position: 'absolute',
                  left: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#94a3b8',
                  pointerEvents: 'none'
                }}
              />
              <select
                value={filterField}
                onChange={(e) => setFilterField(e.target.value)}
                style={{ paddingLeft: '2.75rem' }}
              >
                <option value="all">All Fields</option>
                <option value="itemCode">Item Code</option>
                <option value="itemDesciption">Description</option>
                <option value="supplierAddress">Supplier Address</option>
              </select>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchItems}
              className="btn btn-secondary"
              disabled={loading}
              style={{ justifySelf: 'start' }}
            >
              <RefreshCw size={18} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              Refresh
            </button>

            {/* Import/Export Buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', justifySelf: 'end' }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-success"
              >
                <Upload size={18} />
                Import
              </button>
              <button
                onClick={handleExport}
                className="btn btn-warning"
              >
                <Download size={18} />
                Export
              </button>
              <button
                onClick={deleteAllItems}
                className="btn btn-danger"
                disabled={loading || items.length === 0}
              >
                <Trash2 size={18} />
                Delete All
              </button>
              <button
                onClick={openCreateModal}
                className="btn btn-primary"
              >
                <Plus size={18} />
                Add New
              </button>
            </div>
          </div>

          {/* Stats */}
          <div style={{
            display: 'flex',
            gap: '1.5rem',
            paddingTop: '1rem',
            borderTop: '1px solid #f1f5f9',
            flexWrap: 'wrap'
          }}>
            <div className="badge">
              Total Items:{items.length}
            </div>
            <div className="badge">
              Filtered: {filteredItems.length}
            </div>
            <div className="badge">
              Page {currentPage} of {totalPages || 1}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="glass-card" style={{ marginBottom: '1.5rem' }}>
          <div className="table-container">
            {loading && currentItems.length === 0 ? (
              <div style={{
                padding: '4rem',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div className="spinner"></div>
                <p style={{ color: '#64748b' }}>Loading items...</p>
              </div>
            ) : currentItems.length === 0 ? (
              <div style={{
                padding: '4rem',
                textAlign: 'center'
              }}>
                <p style={{ color: '#64748b', fontSize: '1.1rem' }}>
                  {searchTerm ? 'No items found matching your search.' : 'No items yet. Click "Add New" to get started.'}
                </p>
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('id')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        ID
                        <SortIcon field="id" />
                      </div>
                    </th>
                    <th onClick={() => handleSort('itemCode')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        Item Code
                        <SortIcon field="itemCode" />
                      </div>
                    </th>
                    <th onClick={() => handleSort('itemDesciption')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        Description
                        <SortIcon field="itemDesciption" />
                      </div>
                    </th>
                    <th onClick={() => handleSort('category')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        Category
                        <SortIcon field="category" />
                      </div>
                    </th>
                    <th onClick={() => handleSort('oumSystem')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        OUM System
                        <SortIcon field="oumSystem" />
                      </div>
                    </th>
                    <th onClick={() => handleSort('oumActual')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        OUM Actual
                        <SortIcon field="oumActual" />
                      </div>
                    </th>
                    <th onClick={() => handleSort('supplierAddress')}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        Supplier Address
                        <SortIcon field="supplierAddress" />
                      </div>
                    </th>
                    <th style={{ textAlign: 'center' }}>Actions</th>
                  </tr>

                </thead>
                <tbody>
                  {currentItems.map((item) => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: '600', color: '#3b82f6' }}>#{item.id}</td>
                      <td>{item.itemCode}</td>
                      <td>{item.itemDesciption}</td>
                      <td>{item.category}</td>           {/* ✅ 4th */}
                      <td>{item.oumSystem}</td>          {/* ✅ 5th */}
                      <td>{item.oumActual}</td>          {/* ✅ 6th */}
                      <td>{item.supplierAddress}</td>    {/* ✅ 7th */}
                      <td>
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          justifyContent: 'center'
                        }}>
                          <button
                            onClick={() => openEditModal(item)}
                            className="icon-btn edit"
                            title="Edit"
                          >
                            <Edit2 size={18} color="#64748b" />
                          </button>
                          <button
                            onClick={() => deleteItem(item.id)}
                            className="icon-btn delete"
                            title="Delete"
                          >
                            <Trash2 size={18} color="#64748b" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="glass-card" style={{ padding: '1rem' }}>
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>

              {[...Array(totalPages)].map((_, index) => {
                const pageNumber = index + 1;
                if (
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => setCurrentPage(pageNumber)}
                      className={`page-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  pageNumber === currentPage - 2 ||
                  pageNumber === currentPage + 2
                ) {
                  return <span key={pageNumber} style={{ color: '#94a3b8' }}>...</span>;
                }
                return null;
              })}

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Last
              </button>
            </div>
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content glass-card" onClick={(e) => e.stopPropagation()}>
              <div style={{
                padding: '2rem',
                borderBottom: '1px solid #f1f5f9'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{
                    fontSize: '1.75rem',
                    fontWeight: '600',
                    color: '#1e293b'
                  }}>
                    {editingItem ? 'Edit Item' : 'Add New Item'}
                  </h2>
                  <button
                    onClick={closeModal}
                    className="icon-btn"
                    style={{ padding: '0.5rem' }}
                  >
                    <X size={24} color="#64748b" />
                  </button>
                </div>
              </div>

              <form onSubmit={editingItem ? updateItem : createItem}>
                <div style={{ padding: '2rem' }}>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="itemCode">Item Code *</label>
                    <input
                      id="itemCode"
                      type="text"
                      name="itemCode"
                      value={formData.itemCode}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      placeholder="Enter item code"
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="itemDesciption">Description *</label>
                    <input
                      id="itemDesciption"
                      type="text"
                      name="itemDesciption"
                      value={formData.itemDesciption}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      placeholder="Enter description"
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="supplierAddress">Supplier Address *</label>
                    <input
                      id="supplierAddress"
                      type="text"
                      name="supplierAddress"
                      value={formData.supplierAddress}
                      onChange={handleInputChange}
                      className="input-field"
                      required
                      placeholder="Enter supplier address"
                    />
                  </div>


                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="oumActual">OUM Actual</label>
                    <input
                      id="oumActual"
                      type="text"
                      name="oumActual"
                      value={formData.oumActual}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="Enter OUM Actual"
                    />
                  </div><div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="category">Category</label>
                    <input
                      id="category"
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="Enter category"
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="oumSystem">OUM System</label>
                    <input
                      id="oumSystem"
                      type="text"
                      name="oumSystem"
                      value={formData.oumSystem}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="Enter OUM System"
                    />
                  </div>


                </div>

                <div style={{
                  padding: '1.5rem 2rem',
                  borderTop: '1px solid #f1f5f9',
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                        {editingItem ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      editingItem ? 'Update Item' : 'Create Item'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div className="modal-overlay" onClick={closeImportModal}>
            <div className="modal-content-large glass-card" onClick={(e) => e.stopPropagation()}>
              <div style={{
                padding: '2rem',
                borderBottom: '1px solid #f1f5f9'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{
                    fontSize: '1.75rem',
                    fontWeight: '600',
                    color: '#1e293b'
                  }}>
                    Import Items
                  </h2>
                  <button
                    onClick={closeImportModal}
                    className="icon-btn"
                    style={{ padding: '0.5rem' }}
                  >
                    <X size={24} color="#64748b" />
                  </button>
                </div>
              </div>

              <div style={{ padding: '2rem' }}>
                {/* Summary */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  flexWrap: 'wrap'
                }}>
                  <div className="badge-success">
                    New Items: {importData.length}
                  </div>
                  <div className="badge-warning">
                    Duplicates: {duplicates.length}
                  </div>
                </div>

                {/* Duplicates Section */}
                {duplicates.length > 0 && (
                  <div>
                    <div className="alert alert-warning">
                      <AlertCircle size={20} />
                      <div>
                        <strong>Duplicate Items Found!</strong>
                        <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                          The following items have matching Item Codes. Choose an action for each:
                        </p>
                      </div>
                    </div>

                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {duplicates.map((dup, index) => (
                        <div key={index} className="duplicate-item">
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '1rem',
                            marginBottom: '0.75rem'
                          }}>
                            {/* ✅ EXISTING ITEM */}
                            <div>
                              <strong style={{ color: '#92400e' }}>Existing Item:</strong>
                              <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                                <div><strong>Code:</strong> {dup.existing.itemCode}</div>
                                <div><strong>Desc:</strong> {dup.existing.itemDesciption}</div>
                                <div><strong>Category:</strong> {dup.existing.category || 'N/A'}</div>
                                <div><strong>OUM System:</strong> {dup.existing.oumSystem || 'N/A'}</div>
                                <div><strong>OUM Actual:</strong> {dup.existing.oumActual || 'N/A'}</div>
                                <div><strong>Supplier:</strong> {dup.existing.supplierAddress}</div>
                              </div>
                            </div>

                            {/* ✅ IMPORT ITEM */}
                            <div>
                              <strong style={{ color: '#92400e' }}>Import Item:</strong>
                              <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                                <div><strong>Code:</strong> {dup.itemCode}</div>
                                <div><strong>Desc:</strong> {dup.itemDesciption}</div>
                                <div><strong>Category:</strong> {dup.category || 'N/A'}</div>
                                <div><strong>OUM System:</strong> {dup.oumSystem || 'N/A'}</div>
                                <div><strong>OUM Actual:</strong> {dup.oumActual || 'N/A'}</div>
                                <div><strong>Supplier:</strong> {dup.supplierAddress}</div>
                              </div>
                            </div>
                          </div>

                          <div className="action-buttons">
                            <button
                              className={`action-btn skip ${dup.action === 'skip' ? 'active' : ''}`}
                              onClick={() => handleDuplicateAction(index, 'skip')}
                            >
                              Skip
                            </button>
                            <button
                              className={`action-btn update ${dup.action === 'update' ? 'active' : ''}`}
                              onClick={() => handleDuplicateAction(index, 'update')}
                            >
                              Update Existing
                            </button>
                            <button
                              className={`action-btn delete ${dup.action === 'delete' ? 'active' : ''}`}
                              onClick={() => handleDuplicateAction(index, 'delete')}
                            >
                              Delete Existing
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* New Items Preview */}
                {/* New Items Preview */}
                {importData.length > 0 && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <h3 style={{
                      color: '#1e293b',
                      fontSize: '1.1rem',
                      marginBottom: '0.75rem'
                    }}>
                      New Items to Import ({importData.length})
                    </h3>
                    <div style={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      background: '#f8f9fa',
                      padding: '1rem',
                      borderRadius: '8px'
                    }}>
                      {importData.slice(0, 10).map((item, index) => (
                        <div key={index} style={{
                          fontSize: '0.85rem',
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          background: '#ffffff',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div style={{ fontWeight: '600', color: '#3b82f6', marginBottom: '0.25rem' }}>
                            {item.itemCode}
                          </div>
                          <div style={{ color: '#475569' }}>{item.itemDesciption}</div>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr 1fr',
                            gap: '0.5rem',
                            marginTop: '0.5rem',
                            fontSize: '0.8rem',
                            color: '#64748b'
                          }}>
                            <div>
                              <strong>Category:</strong><br />
                              {item.category || 'N/A'}
                            </div>
                            <div>
                              <strong>OUM System:</strong><br />
                              {item.oumSystem || 'N/A'}
                            </div>
                            <div>
                              <strong>OUM Actual:</strong><br />
                              {item.oumActual || 'N/A'}
                            </div>
                          </div>
                          <div style={{ marginTop: '0.25rem', fontSize: '0.8rem', color: '#64748b' }}>
                            <strong>Supplier:</strong> {item.supplierAddress}
                          </div>
                        </div>
                      ))}
                      {importData.length > 10 && (
                        <div style={{
                          fontSize: '0.9rem',
                          color: '#64748b',
                          marginTop: '0.5rem',
                          textAlign: 'center'
                        }}>
                          ... and {importData.length - 10} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
        
              </div>

              <div style={{
                padding: '1.5rem 2rem',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                gap: '1rem',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={closeImportModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={processImport}
                  className="btn btn-primary"
                  disabled={loading || (importData.length === 0 && duplicates.length === 0)}
                >
                  {loading ? (
                    <>
                      <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                      Processing...
                    </>
                  ) : (
                    'Import Items'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ItemMaintenance;